import { Box, Checkbox, Grid, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Theme } from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSextforceMetricsOnly from '../../../../hooks/useSextforceMetricsOnly';
import { metricTypeName } from '../../../../utils/common';

const MetricRow = ({
    metric,
    thisGroupedMetrics,
    onCheck,
    theme,
}: {
    metric: any;
    thisGroupedMetrics: string[];
    onCheck: (metricId: string, checked: boolean) => void;
    theme: Theme;
}) => {
    return (
        <TableRow
            key={metric._id}
            onClick={() => {
                onCheck(metric._id, !thisGroupedMetrics.includes(metric._id));
            }}
            sx={{
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                },
            }}
        >
            <TableCell align="center">
                <Checkbox
                    id={`metric-${metric._id}`}
                    checked={thisGroupedMetrics.includes(metric._id)}
                    onChange={event => {
                        onCheck(metric._id, event.target.checked);
                    }}
                />
            </TableCell>
            <TableCell>{moment(metric.payload.createdAt).format('L LT')}</TableCell>
            <TableCell>{metric.payload.name || 'No Name'}</TableCell>
            <TableCell>{metricTypeName(metric.type)}</TableCell>
        </TableRow>
    );
};

type Props = {
    targetSubscriberId: string;
    includeCampaigns: boolean;
    includeTrials: boolean;
    includeInactive: boolean;
    groupedMetrics: { [subscriberId: string]: string[] };
    setGroupedMetrics: (groupedMetrics: { [subscriberId: string]: string[] }) => void;
    theme: Theme;
};

const SextforceMetricsGroupDialogSubscriberTab = (props: Props) => {
    const { targetSubscriberId, includeCampaigns, includeTrials, includeInactive, groupedMetrics, setGroupedMetrics, theme } = props;
    const { data: metricsOnly, isLoading: isLoadingMetricsOnly } = useSextforceMetricsOnly(
        targetSubscriberId,
        includeInactive,
        includeCampaigns,
        includeTrials,
        false,
    );

    const [filterText, setFilterText] = useState<string>('');
    const [debouncedFilterText, setDebouncedFilterText] = useState<string>('');

    const thisGroupedMetrics = groupedMetrics[targetSubscriberId] || [];

    // Debounce the filter text
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedFilterText(filterText);
        }, 300);

        return () => clearTimeout(timer);
    }, [filterText]);

    // Filter metrics based on the debounced filter text
    const filteredMetrics = useMemo(() => {
        if (!metricsOnly || !debouncedFilterText.trim()) {
            return metricsOnly || [];
        }

        const searchTerm = debouncedFilterText.toLowerCase().trim();
        return metricsOnly.filter(metric => {
            const name = (metric.payload.name || 'No Name').toLowerCase();
            return name.includes(searchTerm);
        });
    }, [metricsOnly, debouncedFilterText]);

    const selectedFilteredCount = useMemo(
        () => filteredMetrics.filter(metric => thisGroupedMetrics.includes(metric._id)).length,
        [filteredMetrics, thisGroupedMetrics],
    );

    const handleSelectAllChecked = useCallback(
        (checked: boolean) => {
            const filteredIds = filteredMetrics.map(metric => metric._id);

            if (checked) {
                setGroupedMetrics({
                    ...groupedMetrics,
                    [targetSubscriberId]: [...thisGroupedMetrics, ...filteredIds.filter(id => !thisGroupedMetrics.includes(id))],
                });
            } else {
                setGroupedMetrics({
                    ...groupedMetrics,
                    [targetSubscriberId]: thisGroupedMetrics.filter(id => !filteredIds.includes(id)),
                });
            }
        },
        [filteredMetrics, groupedMetrics, targetSubscriberId, thisGroupedMetrics],
    );

    const handleItemChecked = useCallback(
        (metricId: string, checked: boolean) => {
            if (checked) {
                setGroupedMetrics({
                    ...groupedMetrics,
                    [targetSubscriberId]: [...thisGroupedMetrics, metricId],
                });
            } else {
                setGroupedMetrics({
                    ...groupedMetrics,
                    [targetSubscriberId]: thisGroupedMetrics.filter(id => id !== metricId),
                });
            }
        },
        [groupedMetrics, targetSubscriberId, thisGroupedMetrics],
    );

    return (
        <Grid container spacing={0} alignItems={'center'}>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Filter by name..."
                    value={filterText}
                    onChange={event => setFilterText(event.target.value)}
                    sx={{
                        mb: 1,
                        p: 1,
                        '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                        },
                    }}
                />
            </Grid>
            <Grid item xs={12}>
                {isLoadingMetricsOnly ? (
                    <Skeleton variant={'rectangular'} width={'100%'} height={50} />
                ) : (
                    <Box
                        component={'div'}
                        sx={{
                            // scrollable
                            overflow: 'auto',
                            maxHeight: 400,
                        }}
                    >
                        <Table
                            width="100%"
                            size="small"
                            sx={{
                                // no left/right padding on the first and last cells
                                '& td:first-of-type, & th:first-of-type': {
                                    paddingLeft: 0,
                                },
                                // Bold header
                                '& th': {
                                    fontWeight: 'bold',
                                },
                                // No wrapping
                                whiteSpace: 'nowrap',
                                '& td, & th': {
                                    // Small padding on the cells
                                    padding: theme.spacing(0.5),
                                },
                                // sticky header
                                '& thead': {
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1,
                                    bgcolor: 'grey.100',
                                },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell width={45} align="center">
                                        <Checkbox
                                            id="metric-select-all"
                                            checked={filteredMetrics.length > 0 && selectedFilteredCount === filteredMetrics.length}
                                            indeterminate={selectedFilteredCount > 0 && selectedFilteredCount < filteredMetrics.length}
                                            disabled={filteredMetrics.length === 0}
                                            onChange={event => {
                                                handleSelectAllChecked(event.target.checked);
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell width={125}>Created</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell width={100}>Type</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredMetrics &&
                                    filteredMetrics.map(metric => (
                                        <MetricRow
                                            key={metric._id}
                                            metric={metric}
                                            thisGroupedMetrics={thisGroupedMetrics}
                                            onCheck={handleItemChecked}
                                            theme={theme}
                                        />
                                    ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Grid>
        </Grid>
    );
};

export default SextforceMetricsGroupDialogSubscriberTab;
